import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { CheckCircle2, Loader2, TriangleAlert, XCircle } from "lucide-react";
import Logo from "@/components/Logo";
import { dashboardPath, useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";

export default function RegisterPage() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [username, setUsername] = useState("");
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [referralCode, setReferralCode] = useState(searchParams.get("ref") || "");
  const [broker, setBroker] = useState("");
  const [agreeEmail, setAgreeEmail] = useState(false);
  const [agreeRisk, setAgreeRisk] = useState(false);
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [agreeAge, setAgreeAge] = useState(false);
  const [brokers, setBrokers] = useState([]);
  const [referralState, setReferralState] = useState({ status: "idle", name: null });
  const [referralOnly, setReferralOnly] = useState(true);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef(null);

  const validateReferral = async (code) => {
    const trimmed = code.trim();
    if (trimmed.length < 4) {
      setReferralState({ status: "idle", name: null });
      return;
    }
    setReferralState({ status: "checking", name: null });
    try {
      const { data } = await api.get(`/referrals/validate/${encodeURIComponent(trimmed)}`);
      setReferralState(
        data.valid
          ? { status: "valid", name: data.ib_name }
          : { status: "invalid", name: null },
      );
    } catch {
      setReferralState({ status: "idle", name: null });
    }
  };

  useEffect(() => {
    api.get("/site-content").then((res) => setReferralOnly(res.data?.referral_only !== false)).catch(() => {});
    api.get("/brokers").then((res) => setBrokers(res.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => validateReferral(referralCode), 450);
    return () => clearTimeout(debounceRef.current);
  }, [referralCode]);

  const handleUsernameChange = (e) => {
    setUsername(e.target.value.replace(/\D/g, ""));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!broker) {
      toast.error("Please select your registered broker from the list.");
      return;
    }
    setError("");
    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }
    if (!agreeEmail) {
      setError("Please confirm the email agreement below the Email field to create your account.");
      return;
    }
    if (!agreeRisk || !agreeTerms || !agreeAge) {
      setError("Please tick all three consent boxes above the button to create your account.");
      return;
    }
    setLoading(true);
    try {
      const user = await register({
        username,
        full_name: fullName,
        email,
        password,
        confirm_password: confirmPassword,
        referral_code: referralCode,
        registered_broker: broker,
      });
      navigate(dashboardPath(user.role), { replace: true });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050506] flex items-center justify-center px-4 py-12 relative overflow-hidden" data-testid="register-page">
      <div className="absolute bottom-0 left-1/2 -translate-x-1/2 h-[400px] w-[700px] bg-[#D4AF37]/[0.05] blur-[120px] rounded-full pointer-events-none" />
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        className="w-full max-w-lg"
      >
        <div className="flex justify-center mb-8">
          <Logo />
        </div>
        <div className="card-lux p-8 sm:p-10">
          <p className="eyebrow-label mb-3 text-center">Invitation Only</p>
          <h1 className="font-heading text-2xl font-bold text-white text-center mb-2">
            Create Your Account
          </h1>
          <p className="text-xs text-[#64748B] text-center mb-8">
            A valid IB referral code is required to join the ecosystem.
          </p>
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-xs font-medium text-[#94A3B8] mb-2 tracking-wide">
                Username <span className="text-[#64748B]">(numbers only)</span>
              </label>
              <input
                type="text"
                inputMode="numeric"
                value={username}
                onChange={handleUsernameChange}
                className="input-lux font-mono tracking-wider"
                placeholder="e.g. 881208065608"
                required
                minLength={4}
                maxLength={20}
                data-testid="register-username-input"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-[#94A3B8] mb-2 tracking-wide">
                Full Name
              </label>
              <input
                type="text"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                className="input-lux"
                placeholder="Your full name"
                required
                autoComplete="name"
                data-testid="register-fullname-input"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-[#94A3B8] mb-2 tracking-wide">
                Email
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input-lux"
                placeholder="you@email.com"
                required
                autoComplete="email"
                data-testid="register-email-input"
              />
              {/* Email notice + required agreement (frontend validation only) */}
              <div className="mt-3 rounded-lg border border-[#D4AF37]/25 bg-[#D4AF37]/5 px-4 py-3" data-testid="email-warning">
                <p className="text-[10px] font-mono tracking-widest uppercase text-[#D4AF37] mb-1.5 flex items-center gap-1.5">
                  <TriangleAlert className="h-3.5 w-3.5" /> Email Notice
                </p>
                <p className="text-[11px] text-[#94A3B8] leading-relaxed">
                  Please use the same email address registered with your broker. Make sure all registration information is accurate. PlusMaker reserves the right to suspend or delete accounts containing incorrect or misleading information.
                </p>
                <label className="mt-3 flex items-start gap-2.5 cursor-pointer select-none" htmlFor="email-agreement">
                  <input
                    id="email-agreement"
                    type="checkbox"
                    checked={agreeEmail}
                    onChange={(e) => setAgreeEmail(e.target.checked)}
                    className="mt-0.5 h-4 w-4 shrink-0 accent-[#D4AF37] cursor-pointer"
                    data-testid="register-email-agreement-checkbox"
                  />
                  <span className="text-[11px] text-[#B9C0CE] leading-relaxed">
                    I confirm that the email address provided above is the same email address registered with my broker, and I agree to the statement above.
                  </span>
                </label>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-[#94A3B8] mb-2 tracking-wide">
                  Password
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="input-lux"
                  placeholder="Min. 8 characters"
                  required
                  minLength={8}
                  autoComplete="new-password"
                  data-testid="register-password-input"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-[#94A3B8] mb-2 tracking-wide">
                  Confirm Password
                </label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="input-lux"
                  placeholder="Repeat password"
                  required
                  autoComplete="new-password"
                  data-testid="register-confirm-password-input"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-[#94A3B8] mb-2 tracking-wide">
                Sponsor / IB Referral Code {referralOnly ? <span className="text-red-400">*</span> : <span className="text-[#64748B]">(optional)</span>}
              </label>
              <input
                type="text"
                value={referralCode}
                onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
                className="input-lux font-mono tracking-widest uppercase"
                placeholder="e.g. PM482910"
                required={referralOnly}
                data-testid="register-referral-code-input"
              />
              <div className="mt-2 min-h-[20px]" data-testid="referral-validation-status">
                {referralState.status === "checking" && (
                  <p className="text-xs text-[#94A3B8] flex items-center gap-1.5">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" /> Verifying referral code...
                  </p>
                )}
                {referralState.status === "valid" && (
                  <p className="text-xs text-emerald-400 flex items-center gap-1.5" data-testid="referral-valid-message">
                    <CheckCircle2 className="h-3.5 w-3.5" /> Sponsor verified: {referralState.name}
                  </p>
                )}
                {referralState.status === "invalid" && (
                  <p className="text-xs text-red-400 flex items-center gap-1.5" data-testid="referral-invalid-message">
                    <XCircle className="h-3.5 w-3.5" /> Invalid referral code
                  </p>
                )}
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-[#94A3B8] mb-2 tracking-wide">
                Registered Broker <span className="text-red-400">*</span>
              </label>
              <select
                value={broker}
                onChange={(e) => setBroker(e.target.value)}
                className="input-lux font-mono tracking-widest uppercase"
                required
                data-testid="register-broker-select"
              >
                <option value="" disabled>
                  Select your registered broker…
                </option>
                {brokers.map((b) => (
                  <option key={b} value={b} data-testid={`register-broker-option-${b}`}>
                    {b}
                  </option>
                ))}
              </select>
              <div className="mt-3 rounded-lg border border-[#D4AF37]/25 bg-[#D4AF37]/5 px-4 py-3" data-testid="broker-warning">
                <p className="text-[10px] font-mono tracking-widest uppercase text-[#D4AF37] mb-1.5">Important Notice</p>
                <p className="text-[11px] text-[#94A3B8] leading-relaxed">
                  Please select the broker provided or recommended by your IB. Make sure the information is accurate
                  before creating your account. Broker information cannot be changed by the member after registration.
                  Providing incorrect or misleading information may result in account restrictions or suspension by PlusMaker.
                </p>
              </div>
            </div>
            {error && (
              <p className="text-sm text-red-400 bg-red-400/10 border border-red-400/25 rounded-lg px-4 py-3" data-testid="register-error-message">
                {error}
              </p>
            )}
            <div className="rounded-lg border border-white/10 bg-white/[0.02] px-4 py-3.5 space-y-3" data-testid="register-consent-group">
              <label className="flex items-start gap-2.5 cursor-pointer select-none" htmlFor="consent-risk">
                <input
                  id="consent-risk"
                  type="checkbox"
                  checked={agreeRisk}
                  onChange={(e) => setAgreeRisk(e.target.checked)}
                  className="mt-0.5 h-4 w-4 shrink-0 accent-[#D4AF37] cursor-pointer"
                  data-testid="register-consent-risk-checkbox"
                />
                <span className="text-[11px] text-[#B9C0CE] leading-relaxed">
                  I acknowledge that trading involves risk and that past results do not guarantee future results.
                </span>
              </label>
              <label className="flex items-start gap-2.5 cursor-pointer select-none" htmlFor="consent-terms">
                <input
                  id="consent-terms"
                  type="checkbox"
                  checked={agreeTerms}
                  onChange={(e) => setAgreeTerms(e.target.checked)}
                  className="mt-0.5 h-4 w-4 shrink-0 accent-[#D4AF37] cursor-pointer"
                  data-testid="register-consent-terms-checkbox"
                />
                <span className="text-[11px] text-[#B9C0CE] leading-relaxed">
                  I agree to the{" "}
                  <Link to="/terms" target="_blank" rel="noopener noreferrer" className="text-[#D4AF37] underline hover:text-[#E5C76B]" data-testid="register-terms-link">Terms &amp; Conditions</Link>
                  {" "}and acknowledge the{" "}
                  <Link to="/privacy" target="_blank" rel="noopener noreferrer" className="text-[#D4AF37] underline hover:text-[#E5C76B]" data-testid="register-privacy-link">Privacy Policy</Link>.
                </span>
              </label>
              <label className="flex items-start gap-2.5 cursor-pointer select-none" htmlFor="consent-age">
                <input
                  id="consent-age"
                  type="checkbox"
                  checked={agreeAge}
                  onChange={(e) => setAgreeAge(e.target.checked)}
                  className="mt-0.5 h-4 w-4 shrink-0 accent-[#D4AF37] cursor-pointer"
                  data-testid="register-consent-age-checkbox"
                />
                <span className="text-[11px] text-[#B9C0CE] leading-relaxed">
                  I confirm that I am 18 years of age or older.
                </span>
              </label>
            </div>
            <button
              type="submit"
              disabled={loading || referralState.status === "invalid" || referralState.status === "checking" || !agreeRisk || !agreeTerms || !agreeAge}
              className="btn-gold w-full py-3 text-sm disabled:opacity-60"
              data-testid="register-submit-button"
            >
              {loading ? "Creating Account..." : "Create Account"}
            </button>
          </form>
          <div className="gold-divider my-7" />
          <p className="text-sm text-[#64748B] text-center">
            Already a member?{" "}
            <Link to="/login" className="text-[#D4AF37] hover:text-[#E5C158] font-medium" data-testid="register-login-link">
              Login
            </Link>
          </p>
        </div>
        <p className="text-center mt-6">
          <Link to="/" className="text-xs text-[#64748B] hover:text-white transition-colors" data-testid="register-back-home-link">
            ← Back to website
          </Link>
        </p>
      </motion.div>
    </div>
  );
}
